/**
 * caviar-quiz.js — behaviour for sections/caviar-quiz.liquid.
 *
 * THIS FILE OWNS THE MATCHING ALGORITHM. Staff can retune the numbers from the
 * theme editor (points per flavour match, points per value match, how many
 * recommendations to show, and the tin sizes each party size prefers) but the
 * shape of the algorithm — score, filter, sort, top up, pick a variant — is
 * code, not content. Changing it is a developer job.
 *
 * Ported from the React component's getRecommendations()/findBestVariant(),
 * with one deliberate change: candidates come from the collection rendered into
 * the page as JSON and are tagged by product metafields, instead of a hardcoded
 * list of product handles that broke silently whenever a handle was renamed.
 */
(function () {
  'use strict';

  var STORAGE_VERSION = 1;

  function toList(value) {
    if (!value) return [];
    return value
      .split(',')
      .map(function (part) {
        return part.trim();
      })
      .filter(function (part) {
        return part.length > 0;
      });
  }

  /**
   * A product joins the quiz by being in the collection AND carrying quiz
   * metafields — the rule sections/caviar-quiz.liquid states in its own schema,
   * which the matching below did not actually enforce.
   *
   * Two things went wrong without it. "Open to anything" sets flavorMatchAll,
   * which awards the flavour weight to EVERY product in the collection, curated
   * or not. And the top-up loop reaches into the collection in merchandiser
   * order for anything it has not already used. The Caviar collection leads
   * with two 1700g foodservice tins, so both paths recommended a GBP 2,915 trade
   * tin to someone shopping for two people.
   *
   * The React source could not hit this: its candidates were a hardcoded
   * CAVIAR_CATALOG of twelve handles, so an uncurated product was unreachable
   * by construction. Metafields are the better mechanism, but they move that
   * guarantee from the data structure into a check that has to exist.
   */
  function isCurated(product) {
    return (product.flavors || []).length > 0 || (product.values || []).length > 0;
  }

  function readNumber(el, name, fallback) {
    var raw = parseFloat(el.getAttribute(name));
    return isNaN(raw) ? fallback : raw;
  }

  function variantMatchesSize(variant, sizeKeyword) {
    var lower = sizeKeyword.toLowerCase();
    if (variant.title && variant.title.toLowerCase().indexOf(lower) !== -1) return true;
    return (variant.options || []).some(function (option) {
      return String(option).toLowerCase() === lower;
    });
  }

  /** First in-stock variant matching the preferred sizes, else any in-stock, else the first. */
  function findBestVariant(variants, sizeKeywords) {
    for (var i = 0; i < sizeKeywords.length; i++) {
      var keyword = sizeKeywords[i];
      for (var j = 0; j < variants.length; j++) {
        if (variants[j].available && variantMatchesSize(variants[j], keyword)) {
          return variants[j];
        }
      }
    }
    for (var k = 0; k < variants.length; k++) {
      if (variants[k].available) return variants[k];
    }
    return variants[0];
  }

  function CaviarQuiz(root) {
    this.root = root;
    this.products = [];
    this.steps = [];
    this.state = null;
    this.storageKey = root.getAttribute('data-quiz-storage-key') || 'bemka-caviar-quiz';
    this.exclusiveTags = {};
    this.index = 0;
  }

  CaviarQuiz.prototype.init = function () {
    var root = this.root;

    this.products = this.readProducts();

    this.flavorWeight = readNumber(root, 'data-flavor-weight', 10);
    this.valuesWeight = readNumber(root, 'data-values-weight', 5);
    this.resultsCount = Math.max(1, readNumber(root, 'data-results-count', 3));
    this.minResults = readNumber(root, 'data-min-results', 2);
    this.progressLabel = root.getAttribute('data-progress-label') || 'Step [step] of [total]';
    this.sizeLabel = root.getAttribute('data-size-label') || '';
    this.addLabel = root.getAttribute('data-add-label') || 'Add to cart';
    this.soldOutLabel = root.getAttribute('data-sold-out-label') || 'Sold out';
    this.viewLabel = root.getAttribute('data-view-label') || 'View product';
    this.imageSizes = root.getAttribute('data-image-sizes') || '100vw';
    this.cartAddUrl = root.getAttribute('data-cart-add-url') || '/cart/add';

    this.announcer = root.querySelector('[data-quiz-announce]');
    this.resultsEl = root.querySelector('[data-quiz-results]');
    this.noResultsEl = root.querySelector('[data-quiz-no-results]');
    this.pips = Array.prototype.slice.call(root.querySelectorAll('[data-quiz-pip]'));

    this.steps = Array.prototype.slice.call(root.querySelectorAll('[data-quiz-step]')).map(function (el) {
      return { name: el.getAttribute('data-quiz-step'), el: el };
    });
    if (!this.steps.length) return;

    var self = this;
    Array.prototype.forEach.call(root.querySelectorAll('[data-quiz-value]'), function (button) {
      if (button.getAttribute('data-exclusive') === 'true') {
        self.exclusiveTags[button.getAttribute('data-quiz-value')] = true;
      }
    });

    this.state = this.emptyState();
    this.bind();

    // The email step posts a real customer form, so the page reloads. Pick the
    // answers back up from sessionStorage and land where the shopper left off.
    var restored = this.restore();
    if (restored && root.querySelector('[data-quiz-posted]')) {
      this.goToName('result');
      this.revealAfterReturn();
    } else if (restored && root.querySelector('[data-quiz-errors]')) {
      this.goToName('email');
      this.revealAfterReturn();
    } else {
      this.show(0);
    }

    root.dataset.quizReady = 'true';
  };

  /**
   * Bring the quiz back into view after the customer form's round trip.
   *
   * Restoring the step was never the problem — the answers come back fine. The
   * problem is where the shopper is standing. Shopify posts the form to
   * `/contact#contact_form` and returns to `?customer_posted=true#contact_form`,
   * and no element with that id exists on a product page, so the browser gives
   * up and leaves them at the very top. The results are two thousand pixels
   * below, and the whole thing reads as "it just reloaded and lost my answers".
   *
   * Deliberately not `smooth`: the shopper has already watched a page load and
   * a captcha, and animating a 2000px scroll on top of that reads as broken
   * rather than polished. Guarded because older Safari lacks the options form.
   */
  CaviarQuiz.prototype.revealAfterReturn = function () {
    var root = this.root;
    if (!root || !root.scrollIntoView) return;
    window.requestAnimationFrame(function () {
      try {
        root.scrollIntoView({ block: 'start' });
      } catch (e) {
        root.scrollIntoView(true);
      }
    });
  };

  CaviarQuiz.prototype.emptyState = function () {
    return { v: STORAGE_VERSION, servingId: null, sizes: [], flavorTag: null, flavorMatchAll: false, values: [] };
  };

  CaviarQuiz.prototype.readProducts = function () {
    var script = this.root.querySelector('[data-quiz-products]');
    if (!script) return [];
    try {
      var parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  };

  CaviarQuiz.prototype.bind = function () {
    var self = this;
    var root = this.root;

    Array.prototype.forEach.call(root.querySelectorAll('[data-quiz-serving]'), function (button) {
      button.addEventListener('click', function () {
        self.state.servingId = button.getAttribute('data-quiz-serving');
        self.state.sizes = toList(button.getAttribute('data-sizes'));
        self.markSingle('[data-quiz-serving]', button);
        self.persist();
        self.next();
      });
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-quiz-flavor]'), function (button) {
      button.addEventListener('click', function () {
        self.state.flavorTag = button.getAttribute('data-quiz-flavor');
        self.state.flavorMatchAll = button.getAttribute('data-match-all') === 'true';
        self.markSingle('[data-quiz-flavor]', button);
        self.persist();
        self.next();
      });
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-quiz-value]'), function (button) {
      button.addEventListener('click', function () {
        self.toggleValue(button.getAttribute('data-quiz-value'));
        self.syncValueButtons();
        self.persist();
      });
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-quiz-back]'), function (button) {
      button.addEventListener('click', function () {
        self.show(Math.max(0, self.index - 1));
      });
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-quiz-next]'), function (button) {
      button.addEventListener('click', function () {
        // The source defaults an empty values answer to the "unsure" option so
        // the shopper is never scored against values they never chose.
        if (!self.state.values.length) {
          var fallback = Object.keys(self.exclusiveTags)[0];
          if (fallback) {
            self.state.values = [fallback];
            self.syncValueButtons();
          }
        }
        self.persist();
        self.next();
      });
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-quiz-skip]'), function (button) {
      button.addEventListener('click', function () {
        self.goToName('result');
      });
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-quiz-restart]'), function (button) {
      button.addEventListener('click', function () {
        self.reset();
      });
    });

    // The customer form navigates away, so bank the answers first. Delegated
    // on the step so we never have to guess the form's action or markup.
    var emailStep = root.querySelector('[data-quiz-step="email"]');
    if (emailStep) {
      emailStep.addEventListener('submit', function () {
        self.persist();
      });
    }
  };

  CaviarQuiz.prototype.markSingle = function (selector, active) {
    Array.prototype.forEach.call(this.root.querySelectorAll(selector), function (button) {
      button.setAttribute('aria-pressed', button === active ? 'true' : 'false');
    });
  };

  CaviarQuiz.prototype.toggleValue = function (tag) {
    var values = this.state.values;
    if (this.exclusiveTags[tag]) {
      this.state.values = values.indexOf(tag) === -1 ? [tag] : [];
      return;
    }
    var self = this;
    var without = values.filter(function (value) {
      return value !== tag && !self.exclusiveTags[value];
    });
    this.state.values = values.indexOf(tag) === -1 ? without.concat([tag]) : without;
  };

  CaviarQuiz.prototype.syncValueButtons = function () {
    var values = this.state.values;
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-quiz-value]'), function (button) {
      var pressed = values.indexOf(button.getAttribute('data-quiz-value')) !== -1;
      button.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    });
  };

  CaviarQuiz.prototype.indexOfName = function (name) {
    for (var i = 0; i < this.steps.length; i++) {
      if (this.steps[i].name === name) return i;
    }
    return -1;
  };

  CaviarQuiz.prototype.goToName = function (name) {
    var index = this.indexOfName(name);
    this.show(index === -1 ? this.steps.length - 1 : index);
  };

  CaviarQuiz.prototype.next = function () {
    this.show(Math.min(this.steps.length - 1, this.index + 1));
  };

  CaviarQuiz.prototype.show = function (index) {
    var self = this;
    this.index = index;

    this.steps.forEach(function (step, i) {
      step.el.hidden = i !== index;
    });

    this.pips.forEach(function (pip, i) {
      pip.classList.toggle('caviar-quiz__pip--current', i === index);
      pip.classList.toggle('caviar-quiz__pip--done', i < index);
    });

    if (this.steps[index].name === 'result') this.renderResults();

    if (this.announcer) {
      this.announcer.textContent = this.progressLabel
        .replace('[step]', String(index + 1))
        .replace('[total]', String(this.steps.length));
    }

    // Keyboard users need the focus to follow the step, not stay on a button
    // that just disappeared.
    var heading = this.steps[index].el.querySelector('[data-quiz-question]');
    if (heading && document.activeElement !== document.body) {
      window.requestAnimationFrame(function () {
        if (!self.steps[index].el.hidden) heading.focus({ preventScroll: true });
      });
    }
  };

  CaviarQuiz.prototype.reset = function () {
    this.state = this.emptyState();
    this.markSingle('[data-quiz-serving]', null);
    this.markSingle('[data-quiz-flavor]', null);
    this.syncValueButtons();
    this.persist();
    this.show(0);
  };

  /* ---- Matching ------------------------------------------------------ */

  CaviarQuiz.prototype.buildEntry = function (product) {
    if (!product.available) return null;
    var variants = product.variants || [];
    if (!variants.length) return null;
    var variant = findBestVariant(variants, this.state.sizes);
    if (!variant) return null;
    return { product: product, variant: variant };
  };

  CaviarQuiz.prototype.recommend = function () {
    var self = this;
    var effectiveValues = this.state.values.filter(function (tag) {
      return !self.exclusiveTags[tag];
    });

    var candidates = this.products.filter(isCurated);

    var scored = candidates
      .map(function (product) {
        var score = 0;

        if (self.state.flavorMatchAll || (self.state.flavorTag && (product.flavors || []).indexOf(self.state.flavorTag) !== -1)) {
          score += self.flavorWeight;
        }

        if (effectiveValues.length) {
          var matches = effectiveValues.filter(function (tag) {
            return (product.values || []).indexOf(tag) !== -1;
          }).length;
          score += matches * self.valuesWeight;
        }

        return { product: product, score: score };
      })
      .filter(function (row) {
        return row.score > 0;
      });

    // Array#sort is stable, so equal scores keep the merchandiser's collection
    // order — which is the only tiebreak a merchandiser can actually control.
    scored.sort(function (a, b) {
      return b.score - a.score;
    });

    var results = [];
    var seen = {};
    var i;

    for (i = 0; i < scored.length && results.length < this.resultsCount; i++) {
      var entry = this.buildEntry(scored[i].product);
      if (!entry) continue;
      results.push(entry);
      seen[entry.product.handle] = true;
    }

    // Never show an almost-empty result: top up in collection order — from the
    // curated set only, so a top-up can never surface a product the merchandiser
    // has not tagged for the quiz.
    if (results.length < this.minResults) {
      for (i = 0; i < candidates.length && results.length < this.resultsCount; i++) {
        if (seen[candidates[i].handle]) continue;
        var extra = this.buildEntry(candidates[i]);
        if (!extra) continue;
        results.push(extra);
        seen[extra.product.handle] = true;
      }
    }

    return results;
  };

  /* ---- Rendering ----------------------------------------------------- */

  CaviarQuiz.prototype.renderResults = function () {
    if (!this.resultsEl) return;

    var results = this.recommend();
    this.resultsEl.textContent = '';

    if (this.noResultsEl) this.noResultsEl.hidden = results.length > 0;
    this.resultsEl.hidden = results.length === 0;
    this.resultsEl.style.setProperty('--result-columns', String(Math.max(1, results.length)));

    var self = this;
    results.forEach(function (result) {
      self.resultsEl.appendChild(self.buildCard(result));
    });
  };

  CaviarQuiz.prototype.buildCard = function (result) {
    var product = result.product;
    var variant = result.variant;

    var card = document.createElement('div');
    card.className = 'card caviar-quiz__result';

    if (product.badge) {
      var badge = document.createElement('span');
      badge.className = 'caviar-quiz__badge';
      badge.textContent = product.badge;
      card.appendChild(badge);
    }

    if (product.image) {
      var mediaLink = document.createElement('a');
      mediaLink.className = 'media media--zoom caviar-quiz__result-media';
      mediaLink.href = product.url;

      var img = document.createElement('img');
      img.src = product.image;
      if (product.imageSrcset) img.srcset = product.imageSrcset;
      img.sizes = this.imageSizes;
      img.alt = product.imageAlt || product.title;
      img.loading = 'lazy';
      img.decoding = 'async';
      mediaLink.appendChild(img);
      card.appendChild(mediaLink);
    }

    var titleLink = document.createElement('a');
    titleLink.className = 'caviar-quiz__result-title';
    titleLink.href = product.url;
    titleLink.textContent = product.title;
    card.appendChild(titleLink);

    if (this.sizeLabel) {
      var size = document.createElement('p');
      size.className = 'caviar-quiz__result-size';
      size.textContent = this.sizeLabel + ' ' + variant.title;
      card.appendChild(size);
    }

    var price = document.createElement('p');
    price.className = 'caviar-quiz__result-price';
    price.textContent = variant.price;
    card.appendChild(price);

    var actions = document.createElement('div');
    actions.className = 'caviar-quiz__result-actions';

    // A plain form POST to /cart/add, not a hand-rolled fetch: it works the
    // moment it is rendered, needs no cart API contract, and degrades to a
    // normal page navigation.
    var form = document.createElement('form');
    form.className = 'caviar-quiz__result-form';
    form.method = 'post';
    form.action = this.cartAddUrl;

    var idField = document.createElement('input');
    idField.type = 'hidden';
    idField.name = 'id';
    idField.value = String(variant.id);
    form.appendChild(idField);

    var qtyField = document.createElement('input');
    qtyField.type = 'hidden';
    qtyField.name = 'quantity';
    qtyField.value = '1';
    form.appendChild(qtyField);

    var submit = document.createElement('button');
    submit.type = 'submit';
    submit.className = 'btn btn--primary btn--full btn--sm';
    submit.textContent = variant.available ? this.addLabel : this.soldOutLabel;
    submit.disabled = !variant.available;
    form.appendChild(submit);
    actions.appendChild(form);

    var view = document.createElement('a');
    view.className = 'btn btn--secondary btn--full btn--sm';
    view.href = product.url;
    view.textContent = this.viewLabel;
    actions.appendChild(view);

    card.appendChild(actions);
    return card;
  };

  /* ---- Persistence across the customer-form reload -------------------- */

  CaviarQuiz.prototype.persist = function () {
    try {
      window.sessionStorage.setItem(this.storageKey, JSON.stringify(this.state));
    } catch (error) {
      /* Private mode, or storage disabled — the quiz still works in one pass. */
    }
  };

  CaviarQuiz.prototype.restore = function () {
    var raw = null;
    try {
      raw = window.sessionStorage.getItem(this.storageKey);
    } catch (error) {
      return false;
    }
    if (!raw) return false;

    var saved;
    try {
      saved = JSON.parse(raw);
    } catch (error) {
      return false;
    }
    if (!saved || saved.v !== STORAGE_VERSION) return false;

    this.state = {
      v: STORAGE_VERSION,
      servingId: saved.servingId || null,
      sizes: Array.isArray(saved.sizes) ? saved.sizes : [],
      flavorTag: saved.flavorTag || null,
      flavorMatchAll: saved.flavorMatchAll === true,
      values: Array.isArray(saved.values) ? saved.values : []
    };

    if (this.state.servingId) {
      this.markSingle('[data-quiz-serving]', this.root.querySelector('[data-quiz-serving="' + this.state.servingId + '"]'));
    }
    if (this.state.flavorTag) {
      this.markSingle('[data-quiz-flavor]', this.root.querySelector('[data-quiz-flavor="' + this.state.flavorTag + '"]'));
    }
    this.syncValueButtons();
    return true;
  };

  function initAll(scope) {
    var container = scope || document;
    Array.prototype.forEach.call(container.querySelectorAll('[data-quiz-root]'), function (root) {
      if (root.dataset.quizReady === 'true') return;
      new CaviarQuiz(root).init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
